import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getRewardsAllocatorContract } from 'utils/contractHelpers' 
import BigNumber from 'bignumber.js';
import { BIG_ZERO } from 'utils/bigNumber';
import { VoteState } from 'state/types';
import farmsConfig from 'config/constants/farms'

const initialState: VoteState = {
  data: {
    votingPools: [],
    rewardManagerVotes: [],
    userVotesWeighting: 0,
    totalFarmedBalanceAllVotingPools: 0,
    totalAllUserVotes: 0,
    userVotes: [],
    userVotingLpBalance: 0,
    totalVotingLpBalanceLastUserUpdate: 0,
  },
  votesDataLoaded: false,
};

export const fetchPublicVotingDataAsync = createAsyncThunk(
  'voting/fetchPublicVotingData',
  async () => {
    const rewardsAllocatorContract = getRewardsAllocatorContract()

    const votingPoolsCall = rewardsAllocatorContract.getVotingPools();
    const getRewardManagerVotesCall = rewardsAllocatorContract.getRewardManagerVotes()
    const userVotesWeightingCall = rewardsAllocatorContract.userVotesWeighting()
    const totalFarmedBalanceAllVotingPoolsCall = rewardsAllocatorContract.getTotalFarmedAllVotingPools()
    const totalAllUserVotesCall = rewardsAllocatorContract.totalAllUserVotes()

    const [rawVotingPools, rewardManagerVotes, userVotesWeighting, totalAllVotingPools, rawtotalAllUserVotes] = await Promise.all(
      [ votingPoolsCall, getRewardManagerVotesCall, userVotesWeightingCall, totalFarmedBalanceAllVotingPoolsCall, totalAllUserVotesCall])

    const votingPools = rawVotingPools.map((pool: any) => ({
      pid: pool[0].toString(),
      lpAddress: pool[1].toString(),
    }));
    const userWeighting = userVotesWeighting ? userVotesWeighting.toString() : BIG_ZERO.toString()
    const formattedVotes = rewardManagerVotes.map((vote: any) => ({
      pid: vote[0].toString(),
      allocation: vote[1].toString(),
    }));
    const totalFarmedBalanceAllVotingPools = totalAllVotingPools ? totalAllVotingPools.toString() : BIG_ZERO.toString()
    const totalAllUserVotes = rawtotalAllUserVotes ? rawtotalAllUserVotes.toString() : BIG_ZERO.toString()

    return { votingPools, rewardManagerVotes: formattedVotes, userWeighting, totalFarmedBalanceAllVotingPools, totalAllUserVotes };
  }
);

export const fetchUserVotingDataAsync = createAsyncThunk(
  'voting/fetchUserVotingData',
  async (user: string) => {
    const rewardsAllocatorContract = getRewardsAllocatorContract()
    const getUserVotesCall = rewardsAllocatorContract.getUserVotes(user)
    const getUserVotingLpBalanceCall = rewardsAllocatorContract.getUserVotingLpBalance(user)
    const getTotalVotingLpBalanceLastUserUpdateCall = rewardsAllocatorContract.getTotalVotingLpBalanceLastUserUpdate(user)

    const [userVotes, userVotingLpBalance, userVotingLpBalanceLastUserUpdate] = 
      await Promise.all([ getUserVotesCall, getUserVotingLpBalanceCall, getTotalVotingLpBalanceLastUserUpdateCall])

    const userLpBalance = userVotingLpBalance ? userVotingLpBalance.toString() : BIG_ZERO.toString();
    const votingLpBalanceLastUserUpdate = userVotingLpBalanceLastUserUpdate ? new BigNumber(userVotingLpBalanceLastUserUpdate[0]).toString() : BIG_ZERO.toString();
    const formattedUserVotes = userVotes.map((vote: any) => ({
      pid: vote[0].toString(),
      allocation: vote[1].toString(),
    }));

    return { userVotes: formattedUserVotes, userLpBalance, votingLpBalanceLastUserUpdate };
  }
);

const votingSlice = createSlice({
  name: 'farmVoting',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicVotingDataAsync.fulfilled, (state, action) => {
        state.data.votingPools = action.payload.votingPools;
        state.data.rewardManagerVotes = action.payload.rewardManagerVotes;
        state.data.userVotesWeighting = parseFloat(action.payload.userWeighting);
        state.data.totalFarmedBalanceAllVotingPools = parseFloat(action.payload.totalFarmedBalanceAllVotingPools);
        state.data.totalAllUserVotes = parseFloat(action.payload.totalAllUserVotes);
        state.votesDataLoaded = true;
      })
      .addCase(fetchUserVotingDataAsync.fulfilled, (state, action) => {
        state.data.userVotes = action.payload.userVotes;
        state.data.userVotingLpBalance = parseFloat(action.payload.userLpBalance);
        state.data.totalVotingLpBalanceLastUserUpdate = parseFloat(action.payload.votingLpBalanceLastUserUpdate);
      })
  },
});

export default votingSlice.reducer;